import type {
  CreateMedication,
  DoseStatus,
  DoseUnit,
  Medication,
  MedicationReference,
  UpdateMedication,
} from '@adhd/shared';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DoseSlotCard,
  clockNow,
  doseText,
  formatClock,
  formatTime,
  localDay,
  logSlotOptimistic,
} from '../components/DoseSlots.js';
import { ArrowLeftIcon } from '../components/Icons.js';
import { adherencePercent } from '../lib/format.js';
import {
  api,
  ApiError,
  type DoseSlot,
  type AdherenceSummary as AdherenceSummaryType,
  type MedicationsToday,
  type UnscheduledDose,
} from '../lib/api.js';

/**
 * Medications: today's doses first, then the list they come from.
 *
 * Logging is the only thing on this screen that has to be fast. Nothing else in
 * the feature exists without dose data going in, and a log that costs three taps
 * and a wait is a log that stops happening by week two. So every slot carries
 * its three answers in the open — one tap each — and the tap is optimistic: the
 * row changes immediately and only reverts if the server refuses it.
 *
 * The screen deliberately does not keep score. A dose recorded as missed is
 * drawn exactly like one recorded as taken, and a slot whose time has passed
 * with nothing logged says nothing at all. Forgetting medication is the symptom
 * this app is for; an app that turns it into a red mark is worse than no app.
 *
 * And nothing here interprets. It records what was taken and when. Doses,
 * timing, and whether to change anything belong with the prescriber, and the
 * copy points there rather than filling the gap itself.
 */

const DOSE_UNITS: DoseUnit[] = ['mg', 'mcg', 'ml', 'tablet', 'capsule', 'patch', 'other'];

/** 0 = Sunday, matching the server's `daysOfWeek`. */
const DAYS = [
  { value: 0, label: 'Sun' },
  { value: 1, label: 'Mon' },
  { value: 2, label: 'Tue' },
  { value: 3, label: 'Wed' },
  { value: 4, label: 'Thu' },
  { value: 5, label: 'Fri' },
  { value: 6, label: 'Sat' },
];

/**
 * The reference list carries a "Something else" row as a placeholder for
 * free text. It is filtered out of the picker and offered separately, because
 * selecting it would otherwise stamp its key on the medication — and every
 * hand-typed entry would then claim a reference that describes nothing.
 */
const FREE_TEXT_KEY = 'other_custom';

type Editor =
  | { mode: 'pick' }
  | { mode: 'add'; reference: MedicationReference | null }
  | { mode: 'edit'; medication: Medication };

export function Medications(): JSX.Element {
  const navigate = useNavigate();
  const [today, setToday] = useState<MedicationsToday | null>(null);
  const [adherence, setAdherence] = useState<AdherenceSummaryType | null>(null);
  const [meds, setMeds] = useState<Medication[] | null>(null);
  const [reference, setReference] = useState<MedicationReference[] | null>(null);
  const [referenceError, setReferenceError] = useState<string | null>(null);
  const [editor, setEditor] = useState<Editor | null>(null);
  const [loggingExtra, setLoggingExtra] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function reload(): Promise<void> {
    // Stopped medications are asked for too. They are shown in their own
    // section and can be started again, and leaving them out would make a
    // medication someone stopped look deleted — which it never is.
    const [nextToday, nextMeds] = await Promise.all([
      api.medicationsToday(),
      api.listMedications(true),
    ]);
    setToday(nextToday);
    setMeds(nextMeds);
  }

  useEffect(() => {
    // Quietly. A failed adherence fetch must not stop you logging a dose,
    // which is the thing this screen is actually for.
    void api.adherence('?days=30').then(setAdherence).catch(() => {});
    reload().catch((e: unknown) => {
      setError(e instanceof ApiError ? e.message : 'Could not load your medications.');
    });
  }, []);

  /** Record one scheduled dose. Shared with the same row on Today. */
  function logSlot(slot: DoseSlot, status: DoseStatus): void {
    if (!today) return;
    void logSlotOptimistic(today, slot, status, setToday, setError);
  }

  /** A dose taken outside the schedule — as-needed, or off-plan. */
  async function logExtra(medication: Medication): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await api.logDose({
        medicationId: medication.id,
        scheduledFor: null,
        takenAt: null,
        status: 'taken',
        note: null,
      });
      setLoggingExtra(false);
      // Not optimistic like a slot: unscheduled doses stack rather than update,
      // so the list has to come back from the server to stay honest about how
      // many there are.
      await reload();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not save that dose.');
    } finally {
      setBusy(false);
    }
  }

  function openPicker(): void {
    setEditor({ mode: 'pick' });
    if (reference || referenceError) return;
    api
      .medicationReference()
      .then(setReference)
      .catch(() => {
        // Not fatal, and the picker says so. Someone on a train can still add a
        // medication by typing its name, which is the part that matters.
        setReferenceError('Could not load the list of medications.');
      });
  }

  async function restart(medication: Medication): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await api.updateMedication(medication.id, { isActive: true, stoppedOn: null });
      await reload();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not save that.');
    } finally {
      setBusy(false);
    }
  }

  if (!today || !meds) {
    if (error) return <div className="banner banner-error">{error}</div>;
    return (
      <div style={{ display: 'grid', placeItems: 'center', minHeight: '50dvh' }}>
        <div className="spinner" aria-label="Loading" />
      </div>
    );
  }

  if (editor) {
    return (
      <div className="stack-lg stack">
        <header className="page-header">
          <button
            className="btn btn-ghost btn-compact"
            style={{ marginBottom: 'var(--space-3)', paddingLeft: 0 }}
            onClick={() => setEditor(null)}
          >
            <ArrowLeftIcon /> Back to medications
          </button>
          <h1>{editor.mode === 'edit' ? editor.medication.name : 'Add a medication'}</h1>
          <p>
            {editor.mode === 'pick'
              ? 'Pick it from the list, or type in something that is not on it.'
              : editor.mode === 'add'
                ? 'Only the name is needed. The rest can be filled in whenever.'
                : 'Changes apply from here on. Everything already logged stays as it is.'}
          </p>
        </header>

        {editor.mode === 'pick' ? (
          <ReferencePicker
            reference={reference}
            loadError={referenceError}
            onPick={(picked) => setEditor({ mode: 'add', reference: picked })}
            onFreeText={() => setEditor({ mode: 'add', reference: null })}
          />
        ) : (
          <MedicationForm
            medication={editor.mode === 'edit' ? editor.medication : null}
            reference={editor.mode === 'add' ? editor.reference : editor.medication.reference}
            today={today.day}
            onSaved={() => {
              setEditor(null);
              reload().catch(() => {
                setError('Saved, but the list could not be refreshed. Reload the page to see it.');
              });
            }}
          />
        )}
      </div>
    );
  }

  const active = meds.filter((m) => m.isActive);
  const stopped = meds.filter((m) => !m.isActive);
  // The server builds slots medication by medication, so they arrive grouped by
  // drug rather than in the order the day happens. Sorted here, "today" reads
  // top to bottom the way it is lived.
  const slots = [...today.slots].sort((a, b) => a.time.localeCompare(b.time));
  // Only trustworthy when the phone and the account agree about what day it is;
  // a profile timezone set somewhere else would otherwise mark the morning dose
  // as still to come. Suppressed rather than guessed.
  const clockApplies = today.day === localDay(new Date());
  const nowClock = clockNow();

  return (
    <div className="stack-lg stack">
      <header className="page-header">
        <button
          className="btn btn-ghost btn-compact"
          style={{ marginBottom: 'var(--space-3)', paddingLeft: 0 }}
          onClick={() => navigate('/')}
        >
          <ArrowLeftIcon /> Back to today
        </button>
        <h1>Medications</h1>
        <p>What you take, and what you have taken today.</p>
      </header>

      {error && <div className="banner banner-error">{error}</div>}

      {meds.length === 0 ? (
        <div className="card empty stack">
          <p>
            Nothing added yet. Once a medication is here with its times, logging a dose is one tap.
          </p>
          <button
            className="btn btn-primary btn-block"
            style={{ marginTop: 'var(--space-4)' }}
            onClick={openPicker}
          >
            Add a medication
          </button>
        </div>
      ) : (
        <section className="stack">
          <h2>Today</h2>

          {slots.length === 0 ? (
            <div className="card card-quiet">
              <p className="muted">
                {active.length > 0
                  ? 'Nothing scheduled for today. Anything you take can still be logged below.'
                  : 'Nothing scheduled — none of your medications are active at the moment.'}
              </p>
            </div>
          ) : (
            <>
              {/* Said once, at the top, rather than as a caption under every
                  row: the difference between the two words is the only thing
                  about this list that needs explaining. */}
              {/* The assumption has to be stated where the doses are, not
                  buried in a settings note. Someone reviewing this with a
                  prescriber needs to know the default is "taken" before they
                  read a number off it. */}
              <p className="muted">
                A dose whose time has passed counts as taken unless you say otherwise — forgetting
                to log something is not the same as not taking it. Tap Taken to confirm one, or
                Missed or Skipped to correct it. Missed is the day getting away from you, skipped is
                a decision; neither counts against you.
              </p>

              <div className="stack-sm stack">
                {slots.map((slot) => (
                  <DoseSlotCard
                    key={`${slot.medicationId}-${slot.scheduledFor}`}
                    slot={slot}
                    clockApplies={clockApplies}
                    nowClock={nowClock}
                    onLog={logSlot}
                    day={today.day}
                    onTimeChanged={() => void reload().catch(() => {})}
                  />
                ))}
              </div>
            </>
          )}

          {today.unscheduled.length > 0 && (
            <div className="stack-sm stack">
              <h3>Also today</h3>
              {today.unscheduled.map((dose, i) => (
                <div className="card card-quiet row-between" key={`${dose.medicationId}-${i}`}>
                  <strong>{dose.medicationName}</strong>
                  <span className="faint">{describeExtra(dose)}</span>
                </div>
              ))}
            </div>
          )}

          {active.length > 0 &&
            (loggingExtra ? (
              <div className="stack-sm stack">
                <p className="muted">Which one did you take?</p>
                {active.map((m) => (
                  <button
                    key={m.id}
                    className="option"
                    disabled={busy}
                    onClick={() => void logExtra(m)}
                  >
                    <strong>{m.name}</strong>
                    <span>{describeMedication(m)}</span>
                  </button>
                ))}
                <button className="btn btn-ghost btn-block" onClick={() => setLoggingExtra(false)}>
                  Cancel
                </button>
              </div>
            ) : (
              // The only route in for an as-needed dose, and the only route in
              // at all for a medication kept without set times.
              <button className="btn btn-secondary btn-block" onClick={() => setLoggingExtra(true)}>
                Log a dose that is not on the schedule
              </button>
            ))}
        </section>
      )}

      {adherence && <AdherenceSummary summary={adherence} />}

      {meds.length > 0 && (
        <section className="stack">
          <h2>Your medications</h2>

          {active.length === 0 && (
            <p className="muted">Nothing active right now. The ones you stopped are below.</p>
          )}

          <div className="stack-sm stack">
            {active.map((m) => (
              <div className="card" key={m.id}>
                <div className="row-between">
                  <div className="grow" style={{ minWidth: 0 }}>
                    <strong>{m.name}</strong>
                    <p className="faint" style={{ marginTop: 2 }}>
                      {describeMedication(m)}
                    </p>
                    {/* Brand names, because that is what is printed on the box
                        and often the only name anyone has been told. */}
                    {m.reference && m.reference.brandNames.length > 0 && (
                      <p className="faint">Also sold as {m.reference.brandNames.join(', ')}</p>
                    )}
                    <p className="faint">
                      {m.startedOn
                        ? `Started ${formatDate(m.startedOn)}`
                        : 'No start date yet — that is what a before and after lines up against.'}
                    </p>
                  </div>
                  <button
                    className="btn btn-ghost btn-compact"
                    onClick={() => setEditor({ mode: 'edit', medication: m })}
                  >
                    Edit
                  </button>
                </div>
              </div>
            ))}
          </div>

          <button className="btn btn-secondary btn-block" onClick={openPicker}>
            Add a medication
          </button>
        </section>
      )}

      {stopped.length > 0 && (
        <section className="stack">
          <h2>No longer taking</h2>
          {/* The reassurance belongs here, where someone is looking at a
              medication they stopped and wondering what happened to the months
              they logged against it. */}
          <p className="muted">
            Every dose you logged for these is still here, and still counts towards anything the app
            works out from your history.
          </p>
          <div className="stack-sm stack">
            {stopped.map((m) => (
              <div className="card card-quiet" key={m.id}>
                <div className="row-between">
                  <div className="grow" style={{ minWidth: 0 }}>
                    <strong>{m.name}</strong>
                    <p className="faint" style={{ marginTop: 2 }}>
                      {describeMedication(m)}
                      {m.stoppedOn ? ` · stopped ${formatDate(m.stoppedOn)}` : ''}
                    </p>
                  </div>
                  <button
                    className="btn btn-ghost btn-compact"
                    disabled={busy}
                    onClick={() => void restart(m)}
                  >
                    Start again
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      <p className="faint">
        This is a record of what you took, and only that. Anything about doses, timing, or changing
        what you are on is a conversation with whoever prescribes it.
      </p>
    </div>
  );
}

/**
 * The 32-drug reference list, searchable by generic or brand name.
 *
 * Off-label is labelled here, on the row, because this is where the choice is
 * made — and explained once above the list rather than repeated per row. It is
 * information: prescribing off-label is ordinary and common in ADHD care, and a
 * warning tone here would tell someone their prescriber did something odd.
 */
function ReferencePicker({
  reference,
  loadError,
  onPick,
  onFreeText,
}: {
  reference: MedicationReference[] | null;
  loadError: string | null;
  onPick: (reference: MedicationReference) => void;
  onFreeText: () => void;
}): JSX.Element {
  const [query, setQuery] = useState('');

  const needle = query.trim().toLowerCase();
  const matches = (reference ?? [])
    .filter((r) => r.key !== FREE_TEXT_KEY)
    .filter(
      (r) =>
        needle === '' ||
        r.genericName.toLowerCase().includes(needle) ||
        r.brandNames.some((b) => b.toLowerCase().includes(needle)),
    );

  return (
    <div className="stack">
      <div className="field">
        <label htmlFor="med-search">Search</label>
        <input
          id="med-search"
          className="input"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Concerta, guanfacine, melatonin…"
          autoComplete="off"
        />
      </div>

      {loadError && (
        <div className="banner banner-caution">{loadError} You can still type it in below.</div>
      )}

      {!reference && !loadError && (
        <div style={{ display: 'grid', placeItems: 'center', minHeight: '20dvh' }}>
          <div className="spinner" aria-label="Loading" />
        </div>
      )}

      {reference && (
        <p className="muted">
          Some of these are marked off-label, which means they are licensed for something else. In
          ADHD care that is ordinary and common — it is here as information, not a warning.
        </p>
      )}

      <div className="stack-sm stack">
        {matches.map((r) => (
          <button key={r.key} className="option" onClick={() => onPick(r)}>
            <strong>{r.genericName}</strong>
            <span>
              {[
                r.brandNames.length > 0 ? r.brandNames.join(', ') : r.typicalForm,
                r.isOffLabelForAdhd ? 'off-label for ADHD' : null,
              ]
                .filter((part): part is string => part !== null)
                .join(' · ')}
            </span>
          </button>
        ))}
      </div>

      {reference && matches.length === 0 && needle !== '' && (
        <p className="muted">Nothing in the list matches that. It can go in by hand instead.</p>
      )}

      <button className="option" onClick={onFreeText}>
        <strong>Something not on this list</strong>
        <span>Type the name yourself. It tracks exactly the same, minus the brand names.</span>
      </button>
    </div>
  );
}

/**
 * Adding a medication and editing one are the same form.
 *
 * There is deliberately no delete, and there is no longer a destructive one to
 * offer: the server's DELETE archives now, and erasing for real needs an
 * explicit purge. Stopping already does what people mean by "remove it" — off
 * today's list, history intact, reversible — so a second control beside it would
 * only be a way to lose months of the record a review is actually about.
 */
function MedicationForm({
  medication,
  reference,
  today,
  onSaved,
}: {
  medication: Medication | null;
  reference: MedicationReference | null;
  /** The user's own calendar day, resolved server-side in their timezone. */
  today: string;
  onSaved: () => void;
}): JSX.Element {
  const [name, setName] = useState(medication?.name ?? reference?.genericName ?? '');
  const [amount, setAmount] = useState(
    medication && medication.doseAmount !== null ? String(medication.doseAmount) : '',
  );
  const [unit, setUnit] = useState<DoseUnit>(medication?.doseUnit ?? 'mg');
  // The whole schedule entry is kept, not just its time. A time can carry a
  // label, and rebuilding the array from times alone would silently drop any
  // label an entry already had the moment someone opened this to fix a typo.
  const [times, setTimes] = useState<Medication['schedule']>(medication?.schedule ?? []);
  const [days, setDays] = useState<number[]>(medication?.daysOfWeek ?? []);
  const [startedOn, setStartedOn] = useState(medication?.startedOn ?? '');
  const [notes, setNotes] = useState(medication?.notes ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const trimmedAmount = amount.trim();
  const amountValue = trimmedAmount === '' ? null : Number(trimmedAmount);
  // The server rejects zero, negatives and anything above 10000. Caught here so
  // a slipped decimal point comes back as a sentence under the field rather
  // than as a validation error over the whole form.
  const amountValid =
    amountValue === null ||
    (Number.isFinite(amountValue) && amountValue > 0 && amountValue <= 10000);
  const canSave = name.trim() !== '' && amountValid;

  function setTimeAt(index: number, time: string): void {
    setTimes(times.map((entry, i) => (i === index ? { ...entry, time } : entry)));
  }

  async function save(): Promise<void> {
    setBusy(true);
    setError(null);
    const schedule = times
      .filter((entry) => entry.time !== '')
      .sort((a, b) => a.time.localeCompare(b.time));

    try {
      if (medication) {
        const body: UpdateMedication = {
          name: name.trim(),
          doseAmount: amountValue,
          doseUnit: unit,
          schedule,
          daysOfWeek: days,
          startedOn: startedOn || null,
          notes: notes.trim() || null,
        };
        await api.updateMedication(medication.id, body);
      } else {
        const body: CreateMedication = {
          refKey: reference?.key ?? null,
          name: name.trim(),
          doseAmount: amountValue,
          doseUnit: unit,
          schedule,
          daysOfWeek: days,
          startedOn: startedOn || null,
          stoppedOn: null,
          isActive: true,
          notes: notes.trim() || null,
        };
        await api.createMedication(body);
      }
      onSaved();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not save that.');
      setBusy(false);
    }
  }

  async function stop(): Promise<void> {
    if (!medication) return;
    setBusy(true);
    setError(null);
    try {
      await api.updateMedication(medication.id, { isActive: false, stoppedOn: today });
      onSaved();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not save that.');
      setBusy(false);
    }
  }

  return (
    <div className="stack">
      {/* Neutral description of what was picked, so a wrong tap in a list of
          similar names is caught here rather than three weeks into logging it. */}
      {reference && !medication && (
        <div className="card card-quiet">
          <p className="muted">{reference.notes}</p>
        </div>
      )}

      <div className="field">
        <label htmlFor="med-name">Name</label>
        <input
          id="med-name"
          className="input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Whatever you call it"
        />
        {/* Prefilled with the generic name, which is frequently not the word on
            the box. Changing it here changes only what this app calls it. */}
        <p className="hint">Call it whatever you actually call it — the brand name is fine.</p>
      </div>

      <div className="field">
        <label htmlFor="med-amount">Dose</label>
        <input
          id="med-amount"
          className="input"
          type="number"
          inputMode="decimal"
          min={0}
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          placeholder="Optional"
        />
        <div className="chips" role="group" aria-label="Unit">
          {DOSE_UNITS.map((u) => (
            <button key={u} className="chip" aria-pressed={unit === u} onClick={() => setUnit(u)}>
              {u}
            </button>
          ))}
        </div>
        {!amountValid && (
          <p className="hint">That has to be a number, above zero and under 10,000.</p>
        )}
      </div>

      <div className="field">
        <label>Times</label>
        <p className="hint">
          These are what turn up on today's list. Leave them empty if the timing varies — you can
          still log a dose whenever you take one.
        </p>
        <div className="stack-sm stack">
          {times.map((entry, i) => (
            <div className="row" key={i}>
              <input
                className="input"
                type="time"
                value={entry.time}
                aria-label={`Time ${i + 1}`}
                onChange={(e) => setTimeAt(i, e.target.value)}
              />
              <button
                className="btn btn-ghost btn-compact"
                onClick={() => setTimes(times.filter((_, index) => index !== i))}
              >
                Remove
              </button>
            </div>
          ))}
          {/* Eight is the schema's ceiling; asking for a ninth would come back
              as a validation error after everything else was filled in. */}
          {times.length < 8 && (
            <button
              className="btn btn-secondary btn-block"
              onClick={() => setTimes([...times, { time: '08:00' }])}
            >
              Add a time
            </button>
          )}
        </div>
      </div>

      <div className="field">
        <label>Days</label>
        <div className="chips" role="group" aria-label="Days of the week">
          {DAYS.map((day) => (
            <button
              key={day.value}
              className="chip"
              aria-pressed={days.includes(day.value)}
              onClick={() =>
                setDays(
                  days.includes(day.value)
                    ? days.filter((d) => d !== day.value)
                    : [...days, day.value],
                )
              }
            >
              {day.label}
            </button>
          ))}
        </div>
        {days.length === 0 && <p className="hint">Nothing picked means every day.</p>}
      </div>

      <div className="field">
        <label htmlFor="med-started">When did you start it?</label>
        <input
          id="med-started"
          className="input"
          type="date"
          max={today}
          value={startedOn}
          onChange={(e) => setStartedOn(e.target.value)}
        />
        {/* Worth a sentence rather than a shrug: this one field is what the
            before-and-after view keys off, and a blank one leaves that view with
            nothing to divide the history at. The second half is not hedging —
            starting a medication tends to coincide with a diagnosis, a change in
            circumstances, and deciding to do something about it, and all of that
            lands in the "after" too. */}
        <p className="hint">
          Optional, and the one field worth going back for — it is the date a before-and-after
          gets measured either side of. What it shows is what you recorded, not what the
          medication did.
        </p>
      </div>

      <div className="field">
        <label htmlFor="med-notes">Notes</label>
        <textarea
          id="med-notes"
          className="textarea"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Anything worth remembering — how you take it, what your prescriber said."
        />
      </div>

      {error && <div className="banner banner-error">{error}</div>}

      <button
        className="btn btn-primary btn-lg btn-block"
        onClick={() => void save()}
        disabled={busy || !canSave}
      >
        {busy ? <span className="spinner" /> : medication ? 'Save changes' : 'Add it'}
      </button>
      {/* A greyed-out primary button with no stated reason reads as broken. */}
      {name.trim() === '' && (
        <p className="faint" style={{ textAlign: 'center' }}>
          Give it a name and this will save.
        </p>
      )}

      {medication && medication.isActive && (
        <section className="stack-sm stack" style={{ marginTop: 'var(--space-4)' }}>
          <button
            className="btn btn-secondary btn-block"
            onClick={() => void stop()}
            disabled={busy}
          >
            Stop taking this
          </button>
          {/* No confirmation dialogue in front of it. It is reversible and
              nothing is lost, and an "are you sure?" would imply otherwise. */}
          <p className="faint" style={{ textAlign: 'center' }}>
            It comes off today's list. Everything you logged stays, and you can start it again.
          </p>
        </section>
      )}
    </div>
  );
}

/**
 * An off-schedule dose, in the fewest words that stay true.
 *
 * Only "taken" can be written from this screen, but the record can hold any
 * status, and printing the time alone under a heading of things taken today
 * would turn a skipped dose into a taken one.
 */
function describeExtra(dose: UnscheduledDose): string {
  if (dose.status === 'taken' || dose.status === 'late') {
    return dose.takenAt ? formatTime(dose.takenAt) : 'extra dose';
  }
  return dose.status === 'skipped' ? 'skipped' : 'not taken';
}

/** "10 mg · 8:00 and 13:00 · Mon, Wed, Fri" */
function describeMedication(medication: Medication): string {
  const schedule =
    medication.schedule.length > 0
      ? medication.schedule.map((entry) => formatClock(entry.time)).join(' and ')
      : 'no set times';
  const days =
    medication.daysOfWeek.length > 0 && medication.daysOfWeek.length < 7
      ? medication.daysOfWeek
          .slice()
          .sort((a, b) => a - b)
          .map((d) => DAYS.find((day) => day.value === d)?.label ?? '')
          .filter((label) => label !== '')
          .join(', ')
      : null;

  return [doseText(medication.doseAmount, medication.doseUnit), schedule, days]
    .filter((part): part is string => part !== null)
    .join(' · ');
}

/**
 * A `YYYY-MM-DD` as a readable date.
 *
 * Built from its parts rather than handed to `new Date`, which parses a bare
 * date as midnight UTC and then renders it in the reader's zone — dating every
 * start and stop a day early for anyone west of Greenwich.
 */
function formatDate(day: string): string {
  const parts = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day);
  if (!parts) return day;
  const date = new Date(Number(parts[1]), Number(parts[2]) - 1, Number(parts[3]));
  if (Number.isNaN(date.getTime())) return day;
  return date.toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' });
}

/**
 * The last 30 days, as one line.
 *
 * A ratio and nothing else. No streak, no colour that turns bad below a
 * threshold, no target: this is a number to take to a prescriber, and an app
 * that grades someone on it teaches them to log the flattering answer.
 *
 * Days with nothing scheduled are excluded entirely rather than counted as
 * perfect or as zero — before a medication existed there was nothing to adhere
 * to, and either treatment would be a lie about a different thing.
 *
 * Assumed doses are inside the number, because they are inside every other
 * figure the app reports, and said out loud underneath so the reader can weigh
 * it. A clinician being shown 96% deserves to know how much of it nobody
 * actually confirmed.
 */
function AdherenceSummary({ summary }: { summary: AdherenceSummaryType }): JSX.Element | null {
  /*
   * Completed days only.
   *
   * Today is partial by definition: at nine in the morning an evening dose is
   * already in the denominator and cannot possibly be in the numerator yet, so
   * including it makes the rate collapse overnight and climb back by bedtime.
   * A number that swings thirty points because of the time you looked at it is
   * not a measurement of anything. `summary.to` is the owner's own today.
   */
  const days = summary.byDay.filter((d) => d.scheduled > 0 && d.day !== summary.to);
  const scheduledToday = summary.byDay.some((d) => d.scheduled > 0 && d.day === summary.to);

  // Nothing complete yet. Say so rather than vanishing: a section that is simply
  // absent is indistinguishable from one that is broken, and "where is my
  // adherence figure" is a worse question than "it starts tomorrow".
  if (days.length === 0) {
    if (!scheduledToday) return null;
    return (
      <section className="stack">
        <h2>Doses taken · last 30 days</h2>
        <div className="card card-quiet">
          <p className="muted">
            Nothing to report yet — today is still going, and a day is only counted once it is
            over. This starts tomorrow.
          </p>
        </div>
      </section>
    );
  }

  const scheduled = days.reduce((n, d) => n + d.scheduled, 0);
  // `takenScheduled`, not `taken`. The latter counts as-needed doses and omits
  // assumed ones, so it can exceed the number scheduled and it disagreed with
  // the assumed line directly beneath it — this panel briefly showed
  // "0 of 6 doses" above "6 of those counted without being confirmed".
  const taken = days.reduce((n, d) => n + d.takenScheduled, 0);
  const missed = days.reduce((n, d) => n + d.missed, 0);
  const skipped = days.reduce((n, d) => n + d.skipped, 0);
  const assumed = days.reduce((n, d) => n + (d.assumed ?? 0), 0);
  // Clamped rather than divided directly — see adherencePercent. `days` being
  // non-empty guarantees a denominator; it does not guarantee a sane ratio.
  const rate = adherencePercent(taken, scheduled) ?? 0;
  const notTaken = Math.max(0, scheduled - taken);

  return (
    <section className="stack">
      <h2>Doses taken · last 30 days</h2>
      <div className="card stack-sm stack">
        {/* The number says what it measures. "100%" on its own is the kind of
            figure someone repeats to a prescriber having lost track of what it
            was a percentage of, and the honest answer here is often "of two
            doses on one day". */}
        <div className="row-between">
          <div>
            <strong style={{ fontSize: 'var(--text-2xl)', display: 'block' }}>{rate}%</strong>
            <span className="muted">taken{notTaken > 0 && ` · ${100 - rate}% not`}</span>
          </div>
          <span className="faint" style={{ textAlign: 'right' }}>
            {taken} of {scheduled} doses
            <br />
            over {days.length} day{days.length === 1 ? '' : 's'} in the last 30
          </span>
        </div>

        {(missed > 0 || skipped > 0) && (
          <p className="faint">
            {missed > 0 && `${missed} missed`}
            {missed > 0 && skipped > 0 && ' · '}
            {skipped > 0 && `${skipped} skipped on purpose`}
          </p>
        )}

        {assumed > 0 && (
          <p className="faint">
            {assumed} of those counted without being confirmed — the app assumes a dose was taken
            unless you say otherwise.
          </p>
        )}
      </div>
      <p className="faint">
        Today is not counted while it is still going. Days before you added a medication are not
        counted at all. This is a record of what was logged, not a score.
      </p>
    </section>
  );
}
